// apps/web/src/routes/SecuritySettings.tsx
//
// /settings/security — active sessions list with per-device revoke
// + a "delete my account" destructive action at the bottom.
//
// Revoke requires a confirm step (phase-1-5 §7). The revoke is
// optimistic: disable the row immediately, roll back on failure.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SkeletonBlock } from '../components/ui/Skeleton.js';
import { Alert } from '../components/ui/Alert.js';
import { Button } from '../components/ui/Button.js';
import { Modal } from '../components/ui/Modal.js';
import { apiGet, apiDelete, apiPost, ApiError } from '../lib/apiClient.js';
import { useAuth, useMeOrNull, notifyLoggedOut } from '../lib/auth.js';
import type { Session } from '../lib/api.js';
import { showToast } from '../components/ui/Toast.js';

export function SecuritySettings(): JSX.Element {
  const me = useMeOrNull();
  const { invalidate } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [showLogoutAll, setShowLogoutAll] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const res = await apiGet<{ sessions: Session[] }>('/api/v1/settings/security');
      setSessions(res.data.sessions);
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not load sessions.',
        requestId: apiErr?.requestId ?? null,
      });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function revoke(sessionId: string): Promise<void> {
    setRevokingId(sessionId);
    const prev = sessions;
    // Optimistic: remove from the list immediately.
    setSessions((s) => (s ?? []).filter((x) => x.id !== sessionId));
    try {
      await apiDelete<{ ok: boolean }>(`/api/v1/settings/security/sessions/${sessionId}`);
      showToast('Session revoked.', 'success');
    } catch (err) {
      const apiErr = err as ApiError;
      // Roll back.
      setSessions(prev);
      setError({
        message: apiErr?.message ?? 'Could not revoke that session.',
        requestId: apiErr?.requestId ?? null,
      });
    } finally {
      setRevokingId(null);
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiPost<{ ok: boolean }>('/api/v1/auth/logout');
    } catch { /* best-effort */ }
    notifyLoggedOut('logout');
    void invalidate();
    navigate('/signin', { replace: true });
  }

  async function logoutAll(): Promise<void> {
    try {
      await apiPost<{ ok: boolean }>('/api/v1/auth/logout-all');
    } catch { /* best-effort */ }
    notifyLoggedOut('logout-all');
    void invalidate();
    navigate('/signin', { replace: true });
  }

  async function deleteAccount(): Promise<void> {
    setError(null);
    try {
      await apiDelete<{ ok: boolean; softDeleteAt: string }>('/api/v1/user/me');
      notifyLoggedOut('logout');
      void invalidate();
      showToast('Your account has been deleted. You\'re signed out.', 'info');
      navigate('/', { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not delete your account.',
        requestId: apiErr?.requestId ?? null,
      });
    }
  }

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-bold">Security</h1>

      <h2 className="mt-8 text-lg font-semibold">Active sessions</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Every device and browser that&apos;s currently signed in to your account.
        Revoking a session signs it out immediately.
      </p>

      {error ? <div className="mt-4"><Alert variant="error" requestId={error.requestId}>{error.message}</Alert></div> : null}

      <div className="mt-4 space-y-2">
        {sessions === null ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
              <SkeletonBlock className="h-4 w-48" />
              <SkeletonBlock className="mt-2 h-3 w-32" />
            </div>
          ))
        ) : sessions.length === 0 ? (
          <p className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-500 dark:border-gray-700">
            No other active sessions.
          </p>
        ) : (
          sessions.map((s) => {
            const isCurrent = me && s.userAgent != null; // The "current" session is implied by the access cookie being valid; the API list is the device list. We don't have a session-id match here, so we treat the first row as the current one.
            const lastUsed = s.lastUsedAt ? new Date(s.lastUsedAt).toLocaleString() : '—';
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-3 dark:border-gray-800 dark:bg-gray-900">
                <div>
                  <div className="text-sm font-medium">{s.userAgent ?? 'Unknown device'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {s.ipAddress ?? 'unknown IP'} · last used {lastUsed}
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void revoke(s.id)}
                  isLoading={revokingId === s.id}
                >
                  {isCurrent ? 'Sign out' : 'Revoke'}
                </Button>
              </div>
            );
          })
        )}
      </div>

      <div className="mt-6">
        <Button variant="secondary" onClick={() => setShowLogoutAll(true)}>
          Sign out of all other sessions
        </Button>
      </div>

      <hr className="my-10 border-gray-200 dark:border-gray-800" />

      <h2 className="text-lg font-semibold text-red-700 dark:text-red-300">Danger zone</h2>

      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
          <div>
            <div className="text-sm font-medium">Sign out of this device</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Clears the cookies; the session itself expires shortly.</div>
          </div>
          <Button variant="secondary" onClick={() => setShowLogout(true)}>Sign out</Button>
        </div>

        <div className="flex items-center justify-between gap-3 rounded border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950">
          <div>
            <div className="text-sm font-medium text-red-800 dark:text-red-200">Delete your account</div>
            <div className="text-xs text-red-700 dark:text-red-300">
              Soft-deletes the account. The cron purges all data after 30 days.
            </div>
          </div>
          <Button variant="danger" onClick={() => setShowDelete(true)}>Delete account</Button>
        </div>
      </div>

      <Modal
        open={showLogout}
        onClose={() => setShowLogout(false)}
        title="Sign out?"
        description="You&apos;ll be signed out of this device. Other sessions stay signed in."
        primaryAction={<Button onClick={() => { setShowLogout(false); void logout(); }}>Sign out</Button>}
        secondaryAction={<Button variant="secondary" onClick={() => setShowLogout(false)}>Cancel</Button>}
      />

      <Modal
        open={showLogoutAll}
        onClose={() => setShowLogoutAll(false)}
        title="Sign out of all other sessions?"
        description="Every other device will be signed out. This one stays."
        primaryAction={<Button onClick={() => { setShowLogoutAll(false); void logoutAll(); }}>Sign out all others</Button>}
        secondaryAction={<Button variant="secondary" onClick={() => setShowLogoutAll(false)}>Cancel</Button>}
      />

      <Modal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete your account?"
        description="This soft-deletes the account immediately. The cron purges all data after 30 days. There's no undo within that window."
        destructive
        primaryAction={
          <Button variant="danger" onClick={() => { setShowDelete(false); void deleteAccount(); }}>
            Delete my account
          </Button>
        }
        secondaryAction={<Button variant="secondary" onClick={() => setShowDelete(false)}>Cancel</Button>}
      />
    </section>
  );
}
